import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosError,
} from "axios";
import { unifiedStorage } from "./unified-storage";

class HttpInterceptor {
  private client: AxiosInstance;

  constructor() {
    // Use the Doo Events Core API URL from environment or default
    // In development, we use the backend directly on port 3000
    const apiHost =
      import.meta.env.VITE_DOO_EVENTS_API || "http://localhost:3000";
    // Always append /api/v1 to the base URL
    const baseURL = `${apiHost}/api/v1`;

    console.log("[HTTP Interceptor] Initializing with baseURL:", baseURL);

    this.client = axios.create({
      baseURL: baseURL,
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 30000,
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token and event context
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        console.log("[HTTP Interceptor] Request config:", {
          url: config.url,
          baseURL: config.baseURL,
          fullURL: `${config.baseURL}${config.url}`,
          method: config.method,
          headers: config.headers,
        });

        // Add authorization token from unifiedStorage
        const tokens = unifiedStorage.getTokens();
        const token = tokens?.accessToken;
        console.log(
          "[HTTP Interceptor] Token from unifiedStorage:",
          token ? `Found (${token.substring(0, 20)}...)` : "NOT FOUND"
        );

        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
          console.log(
            "[HTTP Interceptor] Authorization header set:",
            config.headers["Authorization"]
          );
        } else {
          console.warn("[HTTP Interceptor] No token found in unifiedStorage!");
        }

        // Add event context headers if available
        const eventId = this.getEventId();
        const userId = this.getUserId();
        const userProfile = this.getUserProfile();

        if (eventId) {
          config.headers["X-Event-Id"] = eventId;
        }
        if (userId) {
          config.headers["X-User-Id"] = userId;
        }
        if (userProfile) {
          config.headers["X-Profile"] = userProfile;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          console.error("Unauthorized access - redirecting to login");

          // Clear auth data
          unifiedStorage.clearTokens();
          unifiedStorage.clearUser();
          unifiedStorage.clearEventId();

          // Redirect to login
          window.location.href = "/login";
        } else if (error.response?.status === 403) {
          console.error("Access forbidden:", error.response?.data);
        } else if ((error.response?.status ?? 0) >= 500) {
          console.error("Server error:", error.response?.data);
        }

        return Promise.reject(error);
      }
    );
  }

  private getEventId(): string | null {
    // Get from URL params or unifiedStorage
    const params = new URLSearchParams(window.location.search);
    return params.get("eventoId") || unifiedStorage.getEventId();
  }

  private getUserId(): string | null {
    const user = unifiedStorage.getUser();
    if (user) {
      return user.id || user._id || user.userId;
    }
    return null;
  }

  private getUserProfile(): string | null {
    const user = unifiedStorage.getUser();
    if (user) {
      return user.profile || user.role || "admin";
    }
    return "admin";
  }

  get axios(): AxiosInstance {
    return this.client;
  }
}

// Create singleton instance
const httpInterceptor = new HttpInterceptor();

// Export the axios instance as httpClient for compatibility
export const httpClient = httpInterceptor.axios;

// Also export the class for testing purposes
export default HttpInterceptor;
